export type DocumentHighlightTarget =
	| string
	| {
			text: string
			pageNumber?: number
			occurrence?: number
	  }

export type RegisteredTextRun = {
	element: HTMLElement
	text: string
}

type TextIndexPosition = {
	node: Text
	startOffset: number
	endOffset: number
}

type CompactTextMatch = {
	start: number
	end: number
}

const minimumFallbackCandidateLength = 48
const minimumFallbackTokenCount = 7
const minimumWindowTokenCount = 10
const fallbackWindowSizes = [48, 32, 20, 12]
const maximumFallbackWindowCandidates = 140

export function normalizeSearchText(value: string) {
	return value
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.replace(/[\u2018\u2019\u201A\u201B]/g, "'")
		.replace(/[\u201C\u201D\u201E\u201F]/g, '"')
		.replace(/[‐‑‒–—―]/g, '-')
		.replace(/[º˚]/g, '°')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase()
}

export function getHighlightText(target: DocumentHighlightTarget) {
	return typeof target === 'string' ? target : target.text
}

export function getOccurrence(target: DocumentHighlightTarget) {
	return typeof target === 'string' ? 1 : Math.max(1, target.occurrence ?? 1)
}

export function getTargetPage(target: DocumentHighlightTarget) {
	return typeof target === 'string' ? undefined : target.pageNumber
}

export function clearMarkedElements(root: HTMLElement) {
	root.querySelectorAll('[data-document-citation-highlight]').forEach(node => {
		node.removeAttribute('data-document-citation-highlight')
		node.classList.remove('document-citation-highlight', 'document-citation-highlight-block')
	})

	root.querySelectorAll('mark[data-document-citation-mark]').forEach(mark => {
		mark.replaceWith(document.createTextNode(mark.textContent ?? ''))
	})
}

export function markRunsByText(runs: RegisteredTextRun[], target: DocumentHighlightTarget) {
	const searchIndex = createPdfSearchIndex(runs)
	const match = findBestCompactTextMatch(searchIndex.text, target)
	if (!match) return null

	const matchedElements = new Set<HTMLElement>()
	for (let index = match.start; index < match.end; index++) {
		const element = searchIndex.elements[index]
		if (element) matchedElements.add(element)
	}

	for (const element of matchedElements) {
		element.dataset.documentCitationHighlight = 'true'
		element.classList.add('document-citation-highlight')
	}

	return matchedElements.values().next().value ?? null
}

export function markTextMatchInRoot(root: HTMLElement, target: DocumentHighlightTarget) {
	const searchIndex = createDomTextSearchIndex(root)
	const match = findBestCompactTextMatch(searchIndex.text, target)
	if (!match) return null

	const groups: Array<{
		node: Text
		startOffset: number
		endOffset: number
		mark?: HTMLElement
	}> = []

	for (let index = match.start; index < match.end; index++) {
		const position = searchIndex.positions[index]
		if (!position) continue
		const previous = groups[groups.length - 1]
		if (previous?.node === position.node) {
			previous.endOffset = position.endOffset
		} else {
			groups.push({
				node: position.node,
				startOffset: position.startOffset,
				endOffset: position.endOffset,
			})
		}
	}

	expandBoundaryPunctuation(groups, getHighlightText(target))

	for (const group of [...groups].reverse()) {
		if (group.startOffset >= group.endOffset) continue
		const range = document.createRange()
		range.setStart(group.node, group.startOffset)
		range.setEnd(group.node, group.endOffset)
		const mark = document.createElement('mark')
		mark.dataset.documentCitationMark = 'true'
		mark.className = 'document-citation-highlight'
		range.surroundContents(mark)
		group.mark = mark
	}

	return groups.find(group => group.mark)?.mark ?? null
}

function expandBoundaryPunctuation(
	groups: Array<{
		node: Text
		startOffset: number
		endOffset: number
	}>,
	targetText: string,
) {
	const trimmedTarget = targetText.trim()
	const leadingPunctuation = trimmedTarget.match(/^[^\p{L}\p{N}]+/u)?.[0] ?? ''
	const trailingPunctuation = trimmedTarget.match(/[^\p{L}\p{N}]+$/u)?.[0] ?? ''
	const firstGroup = groups[0]
	const lastGroup = groups[groups.length - 1]

	if (firstGroup && leadingPunctuation) {
		const nodeText = firstGroup.node.textContent ?? ''
		const candidateStart = Math.max(0, firstGroup.startOffset - leadingPunctuation.length)
		if (nodeText.slice(candidateStart, firstGroup.startOffset) === leadingPunctuation) {
			firstGroup.startOffset = candidateStart
		}
	}

	if (lastGroup && trailingPunctuation) {
		const nodeText = lastGroup.node.textContent ?? ''
		const candidateEnd = Math.min(nodeText.length, lastGroup.endOffset + trailingPunctuation.length)
		if (nodeText.slice(lastGroup.endOffset, candidateEnd) === trailingPunctuation) {
			lastGroup.endOffset = candidateEnd
		}
	}
}

export function markBestBlockMatch(root: HTMLElement, target: DocumentHighlightTarget) {
	const candidates = getHighlightCandidates(getHighlightText(target))
	if (candidates.length === 0) return null
	const blocks = root.querySelectorAll<HTMLElement>('p, li, td, th, h1, h2, h3, h4, h5, h6')

	for (const candidateText of candidates) {
		const normalizedNeedle = normalizeSearchText(candidateText)
		if (!normalizedNeedle) continue
		let seen = 0
		for (const block of blocks) {
			if (!normalizeSearchText(block.textContent ?? '').includes(normalizedNeedle)) continue
			seen += 1
			if (seen !== getOccurrence(target)) continue
			block.dataset.documentCitationHighlight = 'true'
			block.classList.add('document-citation-highlight-block')
			return block
		}
	}

	return null
}

function createPdfSearchIndex(runs: RegisteredTextRun[]) {
	let text = ''
	const elements: HTMLElement[] = []

	for (const run of runs) {
		for (const character of normalizeCompactSearchText(run.text)) {
			text += character
			elements.push(run.element)
		}
	}

	return { text, elements }
}

function createDomTextSearchIndex(root: HTMLElement) {
	let text = ''
	const positions: TextIndexPosition[] = []
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
		acceptNode(node) {
			if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT
			if (node.parentElement?.closest('mark[data-document-citation-mark]')) return NodeFilter.FILTER_REJECT
			return NodeFilter.FILTER_ACCEPT
		},
	})

	let current = walker.nextNode()
	while (current) {
		const node = current as Text
		const nodeText = node.textContent ?? ''
		for (let offset = 0; offset < nodeText.length; ) {
			const codePoint = nodeText.codePointAt(offset)
			if (codePoint == null) break
			const character = String.fromCodePoint(codePoint)
			const nextOffset = offset + character.length
			for (const normalizedCharacter of normalizeCompactSearchText(character)) {
				text += normalizedCharacter
				positions.push({ node, startOffset: offset, endOffset: nextOffset })
			}
			offset = nextOffset
		}
		current = walker.nextNode()
	}

	return { text, positions }
}

function findBestCompactTextMatch(haystack: string, target: DocumentHighlightTarget): CompactTextMatch | null {
	const candidates = getHighlightCandidates(getHighlightText(target))
	for (const candidate of candidates) {
		const needle = normalizeCompactSearchText(candidate)
		if (!needle) continue
		let cursor = -1
		for (let seen = 0; seen < getOccurrence(target); seen++) {
			cursor = haystack.indexOf(needle, cursor + 1)
			if (cursor === -1) break
		}
		if (cursor !== -1) return { start: cursor, end: cursor + needle.length }
	}

	return null
}

function getHighlightCandidates(text: string) {
	const trimmed = text.trim()
	if (!trimmed) return []

	const candidates = [trimmed]
	const fragments = splitHighlightFragments(trimmed)
	for (const fragment of fragments) {
		if (!isSubstantialFallbackCandidate(fragment)) continue
		candidates.push(fragment)
	}

	for (const windowCandidate of getSlidingWindowCandidates(trimmed)) {
		candidates.push(windowCandidate)
	}

	const seen = new Set<string>()
	return candidates.filter(candidate => {
		const key = normalizeCompactSearchText(candidate)
		if (!key || seen.has(key)) return false
		seen.add(key)
		return true
	})
}

function isSubstantialFallbackCandidate(text: string) {
	return (
		normalizeCompactSearchText(text).length >= minimumFallbackCandidateLength &&
		getSearchTokens(text).length >= minimumFallbackTokenCount
	)
}

function getSlidingWindowCandidates(text: string) {
	const tokens = getSearchTokens(text)
	if (tokens.length < minimumWindowTokenCount) return []

	const candidates: string[] = []
	for (const requestedWindowSize of fallbackWindowSizes) {
		const windowSize = Math.min(requestedWindowSize, tokens.length)
		if (windowSize < minimumWindowTokenCount) continue
		const step = Math.max(4, Math.floor(windowSize / 2))
		for (let start = 0; start + windowSize <= tokens.length; start += step) {
			candidates.push(tokens.slice(start, start + windowSize).join(' '))
			if (candidates.length >= maximumFallbackWindowCandidates) return candidates
		}

		const finalStart = tokens.length - windowSize
		if (finalStart > 0 && finalStart % step !== 0) candidates.push(tokens.slice(finalStart).join(' '))
		if (candidates.length >= maximumFallbackWindowCandidates) return candidates
	}

	return candidates
}

function getSearchTokens(text: string) {
	return normalizeSearchText(text).match(/[\p{Letter}\p{Number}]+/gu) ?? []
}

function splitHighlightFragments(text: string) {
	const normalized = text.replace(/\r\n?/g, '\n')
	const fragments: string[] = []

	for (const line of normalized.split(/\n+/)) {
		const cleanLine = cleanFragment(line)
		if (cleanLine) fragments.push(cleanLine)
	}

	for (const segment of splitSentenceAndBulletFragments(normalized)) {
		const cleanSegment = cleanFragment(segment)
		if (cleanSegment) fragments.push(cleanSegment)
	}

	return fragments
}

function splitSentenceAndBulletFragments(text: string) {
	const fragments: string[] = []
	let start = 0

	for (let index = 0; index < text.length; ) {
		const codePoint = text.codePointAt(index)
		if (codePoint == null) break
		const character = String.fromCodePoint(codePoint)
		const nextIndex = index + character.length

		if (isBulletMarker(character) && isOnlyWhitespace(text.slice(start, index))) {
			start = nextIndex
			index = nextIndex
			continue
		}

		if (isSentenceBoundary(character) && isWhitespaceAt(text, nextIndex)) {
			fragments.push(text.slice(start, nextIndex))
			start = skipWhitespace(text, nextIndex)
			index = start
			continue
		}

		if (isBulletMarker(character) && isWhitespaceAt(text, index - 1)) {
			const previous = text.slice(start, index)
			if (previous.trim()) fragments.push(previous)
			start = nextIndex
			index = nextIndex
			continue
		}

		index = nextIndex
	}

	const finalFragment = text.slice(start)
	if (finalFragment.trim()) fragments.push(finalFragment)
	return fragments
}

function isSentenceBoundary(character: string) {
	return character === '.' || character === '!' || character === '?' || character === ':' || character === ';'
}

function isBulletMarker(character: string) {
	return character === '•' || character === '*'
}

function isOnlyWhitespace(value: string) {
	return value.trim().length === 0
}

function isWhitespaceAt(value: string, index: number) {
	if (index < 0 || index >= value.length) return false
	return /\s/u.test(value[index] ?? '')
}

function skipWhitespace(value: string, index: number) {
	let nextIndex = index
	while (nextIndex < value.length && /\s/u.test(value[nextIndex] ?? '')) nextIndex += 1
	return nextIndex
}

function cleanFragment(text: string) {
	return text
		.replace(/^\s*[•*\-]\s*/, '')
		.replace(/\s+/g, ' ')
		.trim()
}

function normalizeCompactSearchText(value: string) {
	return normalizeSearchText(value).replace(/[^\p{Letter}\p{Number}]+/gu, '')
}
